import { Template } from "@/types/document-generator";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Skeleton } from "@/components/ui/skeleton";
import { FileText } from "lucide-react";

interface TemplateSelectionTabProps {
  templates: Template[];
  templatesLoading: boolean;
  onSelectTemplate: (template: Template) => void;
}

export function TemplateSelectionTab({ 
  templates, 
  templatesLoading,
  onSelectTemplate 
}: TemplateSelectionTabProps) {
  // Group templates by category
  const groupedTemplates = templates?.reduce((acc: Record<string, Template[]>, template) => {
    if (!acc[template.category]) {
      acc[template.category] = [];
    }
    acc[template.category].push(template);
    return acc;
  }, {}) || {};

  return (
    <ScrollArea className="flex-1 h-[60vh]">
      <div className="p-6 space-y-8">
        {templatesLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-32" />
            ))}
          </div>
        ) : (
          Object.entries(groupedTemplates).map(([category, categoryTemplates]) => (
            <div key={category} className="space-y-4">
              <h3 className="text-lg font-semibold text-muted-foreground capitalize">{category}</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {categoryTemplates.map((template) => (
                  <button
                    key={template.id}
                    onClick={() => onSelectTemplate(template)}
                    className="flex flex-col text-left p-4 rounded-lg border bg-white hover:bg-accent/5 transition-colors cursor-pointer group"
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <FileText className="h-4 w-4 text-amber-600 group-hover:scale-110 transition-transform" />
                      <h4 className="font-medium">{template.title}</h4>
                    </div>
                    {template.description && (
                      <p className="text-sm text-muted-foreground line-clamp-2">
                        {template.description}
                      </p>
                    )}
                  </button>
                ))}
              </div>
            </div>
          ))
        )}
      </div>
    </ScrollArea>
  );
}